import React, { useEffect, useState } from "react";
import Pagination from "react-js-pagination";
import { useSearchParams } from "react-router-dom";
import useFullPageLoader from "../../hooks/useFullPageLoader";
import useDebounce from "../../hooks/useDebounce";
import NotFound from "../../components/not-found/NotFound";
import Switch from "../../components/Switch/Switch";
import { IoSearch } from "react-icons/io5";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import IconButton from "../../components/Button/IconButton";
import { toastMessage } from "../../utils/toastMessage";
import common from "../../routes/common";
import scrollToUp from "../../utils/scrollToUp";
import findSerialNumber from "../../utils/findSerialNumber";
import ModalBox from "../../components/modal/ModalBox";
import { Input, Select } from "../../components/Form";
import useButtonLoader from "../../hooks/UseButtonLoader";
import {
  fetchOfferVariantList,
  createOfferVariant,
  updateOfferVariant,
} from "../../services/offerEngine";

const { NOT_FOUND } = common;

function deviceSummary(userAgent = "") {
  const agent = String(userAgent || "");
  const os = /Windows/i.test(agent) ? "Windows"
    : /Mac OS|Macintosh/i.test(agent) ? "Mac"
    : /Android/i.test(agent) ? "Android"
    : /iPhone|iPad/i.test(agent) ? "iOS"
    : "";
  const browser = /Edg\//i.test(agent) ? "Edge"
    : /Chrome\//i.test(agent) ? "Chrome"
    : /Firefox\//i.test(agent) ? "Firefox"
    : /Safari\//i.test(agent) ? "Safari"
    : "";
  return [os, browser].filter(Boolean).join(" · ");
}

function formatAuditTime(value) {
  if (!value) return "";
  return new Date(value).toLocaleString("en-IN", { hour12: true, timeZone: "Asia/Kolkata" });
}

const MODE_OPTIONS = [
  { value: "CHAT", label: "Chat" },
  { value: "CALL", label: "Call" },
];
const TYPE_OPTIONS = [
  { value: "PER_MINUTE", label: "Per Minute" },
  { value: "FIXED", label: "Fixed" },
  { value: "PERCENT", label: "Percentage Discount" },
];

const emptyVariant = { name: "", description: "", type: "", price: "", duration: "", mode: "", isActive: true, connectButtonText: "" };

const OfferVariantList = () => {
  const [searchParam, setSearchParam] = useSearchParams();
  const activePage = +searchParam.get("page") || 1;
  const [search, setSearch] = useState("");
  const debounceValue = useDebounce(search, 300);
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [loader, showLoader, hideLoader] = useFullPageLoader();

  // modal states
  const [showModal, setShowModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [formData, setFormData] = useState(emptyVariant);
  const [error, setError] = useState({});
  const [buttonRef, buttonLoader] = useButtonLoader("Save");

  // ─── Fetch ────────────────────────────────────────────────────────
  const fetchList = async () => {
    try {
      showLoader();
      const params = { page: activePage, limit: 10 };
      if (debounceValue) { params.search = debounceValue; params.page = 1; }
      const { data } = await fetchOfferVariantList(params);
      if (data.code === 200) {
        setList(data.result?.list || []);
        setTotal(data.result?.total || 0);
        scrollToUp();
      } else {
        setList([]); setTotal(0);
        toastMessage(data.message, "error", "variantList");
      }
    } catch (e) { console.error(e); }
    finally { hideLoader(); }
  };

  useEffect(() => { fetchList(); }, [activePage, debounceValue]);

  // ─── Pagination ───────────────────────────────────────────────────
  const handlePageChange = (page) => { searchParam.set("page", page); setSearchParam(searchParam); };

  // ─── Modal ────────────────────────────────────────────────────────
  const openAdd = () => { setIsEdit(false); setFormData(emptyVariant); setError({}); setShowModal(true); };
  const openEdit = (item) => { setIsEdit(true); setFormData({ ...item }); setError({}); setShowModal(true); };
  const closeModal = () => { setShowModal(false); setFormData(emptyVariant); setError({}); };

  const onFieldChange = (key, value) => setFormData((prev) => ({ ...prev, [key]: value }));

  // ─── Validation ───────────────────────────────────────────────────
  const validate = () => {
    const errs = {};
    if (!formData.name) errs.name = "Name is required";
    if (!formData.type) errs.type = "Type is required";
    if (!formData.price && formData.price !== 0) errs.price = "Price is required";
    if (!formData.duration && formData.duration !== 0) errs.duration = "Duration is required";
    if (!formData.mode) errs.mode = "Mode is required";
    setError(errs);
    return Object.keys(errs).length === 0;
  };

  // ─── Submit ───────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!validate()) return;
    try {
      buttonLoader(true);
      const payload = {
        ...formData,
        price: Number(formData.price),
        duration: Number(formData.duration),
      };
      const apiFn = isEdit ? updateOfferVariant : createOfferVariant;
      const { data } = await apiFn(payload);
      if (data.code === 200) {
        toastMessage(data.message, "success", "variantSave");
        closeModal();
        fetchList();
      } else {
        toastMessage(data.message, "error", "variantSave");
      }
    } catch (e) { console.error(e); }
    finally { buttonLoader(false); }
  };

  // ─── Toggle active ────────────────────────────────────────────────
  const toggleActive = async (item) => {
    try {
      const { data } = await updateOfferVariant({ _id: item._id, isActive: !item.isActive });
      if (data.code === 200) { toastMessage("Status updated", "success", "variantStatus"); fetchList(); }
      else toastMessage(data.message, "error", "variantStatus");
    } catch (e) { console.error(e); }
  };

  return (
    <>
      {loader}
      <div className="WrapperArea">
        <div className="WrapperBox">
          <div className="TitleBox">
            <h4 className="Title">Offer Variants</h4>
            <div className="block_user_button">
              <button onClick={openAdd}><AddIcon /> Add Variant</button>
            </div>
          </div>

          <div className="FilterBox FilterArea">
            <div className="form-group search_lg_size userSearch">
              <input
                type="search"
                className={`form-control ${search && "active"}`}
                placeholder="Search by name"
                value={search}
                style={{ "--color": "#FF8989" }}
                onChange={(e) => setSearch(e.target.value)}
              />
              <span className="user_search_icon"><IoSearch /></span>
            </div>
          </div>

          <div className="content-table">
            <table>
              <thead>
                <tr>
                  <th>S.No.</th>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Price (₹)</th>
                  <th>Duration (sec)</th>
                  <th>Mode</th>
                  <th>Created by</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {list.length > 0 ? (
                  list.map((item, i) => (
                    <tr key={item._id}>
                      <td>{findSerialNumber(i, activePage, 10)}</td>
                      <td>{item.name}</td>
                      <td>{item.type}</td>
                      <td>{item.price}</td>
                      <td>{item.duration}</td>
                      <td>{item.mode}</td>
                      <td style={{ fontSize: "12px", minWidth: 160 }}>
                        {item.createdBy?.name || item.createdBy?.email ? (
                          <>
                            <div>{item.createdBy.name || "—"}</div>
                            <div>{item.createdBy.email || ""}</div>
                            <div>{deviceSummary(item.createdBy.userAgent) || "—"}</div>
                            <div>{item.createdBy.ipAddress || ""}</div>
                            <div>{formatAuditTime(item.createdAt)}</div>
                          </>
                        ) : "—"}
                      </td>
                      <td>
                        <div className="action_button">
                          <Switch checked={item.isActive} onChange={() => toggleActive(item)} />
                          <IconButton className="px-2 py-0" onClick={() => openEdit(item)}>
                            <EditIcon className="edit_icon" />
                          </IconButton>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <NotFound cols={8} msg="No variants found" icon={NOT_FOUND} loader={loader} />
                )}
              </tbody>
            </table>
          </div>

          {total > 10 && (
            <div className="pagination-wrapper">
              <div className="Pagination">
                <Pagination activePage={activePage} previousLabel="previous" nextLabel="next" itemsCountPerPage={10} totalItemsCount={total} pageRangeDisplayed={5} onChange={handlePageChange} hideDisabled />
              </div>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <ModalBox
          status={showModal}
          onClose={closeModal}
          title={isEdit ? "Edit Variant" : "Add Variant"}
          closeIcon
          size="sm"
          custom
          customizedFooter={
            <div className="model_custom_footer">
              <button className="btn_size" ref={buttonRef} onClick={handleSubmit}>Save</button>
            </div>
          }
          category
        >
          <div>
            <div className="form-group mb-2">
              <Input type="text" label="Name" placeholder="Variant name" className="form-control" value={formData.name} onChange={(e) => onFieldChange("name", e.target.value)} error={!formData.name && error.name} required maxLength={60} />
            </div>
            <div className="form-group mb-2">
              <Input type="text" label="Description" placeholder="Variant description" className="form-control" value={formData.description} onChange={(e) => onFieldChange("description", e.target.value)} error={!formData.description && error.description} maxLength={60} />
            </div>
            <div className="form-group mb-2">
              <Select label="Type" className="form-control" value={formData.type} onChange={(e) => onFieldChange("type", e.target.value)} error={!formData.type && error.type} required>
                <option value="">Select Type</option>
                {TYPE_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
              </Select>
            </div>
            <div className="form-group mb-2">
              <Input type="number" label="Price (₹)" placeholder="0" className="form-control" value={formData.price} onChange={(e) => onFieldChange("price", e.target.value)} error={!formData.price && error.price} required />
            </div>
            <div className="form-group mb-2">
              <Input type="number" label="Duration (seconds)" placeholder="0" className="form-control" value={formData.duration} onChange={(e) => onFieldChange("duration", e.target.value)} error={!formData.duration && error.duration} required />
            </div>
            <div className="form-group mb-2">
              <Select label="Mode" className="form-control" value={formData.mode} onChange={(e) => onFieldChange("mode", e.target.value)} error={!formData.mode && error.mode} required>
                <option value="">Select Mode</option>
                {MODE_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
              </Select>
            </div>
            <div className="form-group mb-2">
              <Input type="text" label="Connect Button Text (optional)" placeholder="e.g. Connect Now @₹99" className="form-control" value={formData.connectButtonText || ""} onChange={(e) => onFieldChange("connectButtonText", e.target.value)} maxLength={80} />
            </div>
          </div>
        </ModalBox>
      )}
    </>
  );
};

export default OfferVariantList;
